"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFingerprintId = void 0;
const FingerprintJS = __importStar(require("@fingerprintjs/fingerprintjs-pro"));
const CACHE_KEY = "CACHED_FP_VALUE";
const TIMEOUT_SENTINEL = Symbol("TIMEOUT_SENTINEL");
const FETCH_TIMEOUT = 5000;
const TRANSIENT_TIMEOUT = 1000;
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["None"] = 0] = "None";
    LogLevel[LogLevel["All"] = 1] = "All";
})(LogLevel || (LogLevel = {}));
/**
 * Useful for debugging.
 */
const logLevel = LogLevel.None;
const log = (...data) => {
    if (logLevel !== LogLevel.None) {
        console.log(...data);
    }
};
/**
 * Reads the value of the cookie with a given name.
 * Utility function taken from https://www.quirksmode.org/js/cookies.html
 */
function readCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == " ")
            c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) == 0)
            return c.substring(nameEQ.length, c.length);
    }
    return null;
}
const fetchAndCacheInCookies = async (fetcher) => {
    const cachedFpValue = readCookie(CACHE_KEY);
    if (cachedFpValue) {
        log(`Fingerprint cache hit: ${CACHE_KEY}`);
        return cachedFpValue;
    }
    log(`Fingerprint cache miss: ${CACHE_KEY}`);
    const result = await fetcher();
    if (result == undefined) {
        // Don't cache failed results
        return undefined;
    }
    const domainForCaching = maybeGetDomainForCaching();
    /**
     * Set expiry for a full year.
     */
    const expiry = new Date();
    expiry.setFullYear(expiry.getFullYear() + 1);
    const expiryUtcString = expiry.toUTCString();
    document.cookie = `${CACHE_KEY}=${result};expires=${expiryUtcString};max-age=60*60*24*365;${domainForCaching ? `domain=${domainForCaching};` : ""}${window.location.protocol === "https" ? "secure;" : ""}`;
    return result;
};
/**
 * If the call still takes longer than 5 seconds, we should terminate and fallback
 */
const fetchWithTimeout = async (fetcher) => {
    const resultWithTimeout = await Promise.race([
        fetcher(),
        new Promise((resolve) => setTimeout(() => resolve(TIMEOUT_SENTINEL), FETCH_TIMEOUT)),
    ]);
    if (resultWithTimeout === TIMEOUT_SENTINEL || typeof resultWithTimeout === "symbol") {
        return undefined;
    }
    return resultWithTimeout;
};
/**
 * If the call fails in the first 1 second, we should try straight away
 */
const fastFailFetch = async (fetcher) => {
    const timeStartInMs = new Date().getTime();
    try {
        const result = await fetcher();
        return result;
    }
    catch (e) {
        const timeEndInMs = new Date().getTime();
        if (timeEndInMs - timeStartInMs < TRANSIENT_TIMEOUT) {
            log(`Failed to fetch in under ${TRANSIENT_TIMEOUT}ms; trying again`);
            return fetcher();
        }
        else {
            throw e;
        }
    }
};
/**
 * If there's an repeated error in fetching, we should just return undefined.
 */
const fetchWithErrorFallback = async (fetcher) => {
    try {
        const result = await fetcher();
        return result;
    }
    catch (error) {
        return undefined;
    }
};
/**
 * If on a Qwilr domain, we should be caching the ID across
 * all subdomains. Else, just use the default tenancy.
 */
const maybeGetDomainForCaching = () => {
    const { hostname } = window.location;
    if (hostname === "qwilr.com" || hostname.endsWith(".qwilr.com")) {
        return "qwilr.com";
    }
    return undefined;
};
/**
 * Wrapper for fetching a Fingerprint device ID from Fingerprint's API.
 * Handles caching and error handling in a common and consistent way.
 *
 * Failures or skipping fetching will result in an `undefined` return value; these
 * should be handled by the consumer (most likely by just falling back to a standard
 * Segment anonymous ID).
 *
 * @param apiKey {optional}: The API key from the Fingerprint dashboard. If not provided,
 *  a call will not be attempted.
 */
const getFingerprintId = async (apiKey) => {
    if (!apiKey) {
        return undefined;
    }
    return fetchAndCacheInCookies(() => fetchWithTimeout(() => fetchWithErrorFallback(() => fastFailFetch(async () => {
        const fpAgent = await FingerprintJS.load({
            apiKey: apiKey,
            endpoint: ["https://fp.qwilr.com", FingerprintJS.defaultEndpoint],
        });
        const result = await fpAgent.get();
        return result.visitorId;
    }))));
};
exports.getFingerprintId = getFingerprintId;
